import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { isEmpty, isNil } from 'lodash'

import { useFlags } from 'launchdarkly-react-client-sdk'
import { fetchingAPI, apiService } from '@/api'
import { Button } from '@/components/buttons/Button'
import { SegmentedControl } from '@/components/buttons/SegmentedControl'
import { Checkbox } from '@/components/forms/Checkbox'
import { MultiSelect } from '@/components/forms/MultiSelect'
import { toast } from '@/components/toasts/toast.helpers'
import { fetchCurrentOrgFilterOptions } from '@/reducers/notifications/notifications.actions'

import {
  ALL_NOTIFICATION_TYPES,
  visibleNotificationCategories,
} from './notificationTypes.constants'

import './NotificationSettings.scss'

// v1: one subscription per user. The whole notification setup — agent
// filter + per-event/per-channel matrix — lives on the same row and
// saves together. This component renders both pieces in one modal.

// Backend channel keys → user-facing labels. The /available-channels
// endpoint returns the keys; we render whichever ones the org has
// access to with the friendlier name.
const CHANNEL_LABELS = {
  in_app: 'Balto cloud',
  slack: 'Slack',
  email: 'Email',
  teams: 'Teams',
}

const TARGET_OPTIONS = [
  { key: 'specific_users', label: 'Specific Users' },
  { key: 'tags', label: 'By Tag' },
  { key: 'all_users', label: 'All Agents in Organization' },
]

const inferTargetType = (settings) => {
  if (!settings) {
    return 'specific_users'
  }
  if (settings.all_users) {
    return 'all_users'
  }
  if (!isEmpty(settings.tag_ids)) {
    return 'tags'
  }
  return 'specific_users'
}

// Pure helper: produces the events_matrix object the backend expects
// from the form's per-(type, channel) toggle state.
export const buildEventsMatrix = (toggles, availableTypes, availableChannels) => {
  const matrix = {}
  availableTypes.forEach((type) => {
    const channels = {}
    availableChannels.forEach((channel) => {
      channels[channel] = Boolean(toggles[type.key]?.[channel])
    })
    matrix[type.key] = channels
  })
  return matrix
}

const isRowEnabled = (toggles, typeKey, availableChannels) =>
  availableChannels.some((channel) => toggles[typeKey]?.[channel])

export const NotificationSettings = ({
  // Admin mode: pre-bound to a list of recipients (users + tags) the
  // admin form picked at the top (Image 3). When either is set, saving
  // POSTs to /admin/settings instead of PUT /settings. The filter
  // section ("Receive notifications from") still renders so the admin
  // can configure both *who* gets notified and *which agents* trigger.
  adminTargetUserIds = null,
  adminTargetTagIds = null,
  onSaved = () => {},
}) => {
  const isAdminMode = adminTargetUserIds !== null || adminTargetTagIds !== null
  const dispatch = useDispatch()
  const flags = useFlags()
  // A category whose feature is off would show a row the user can toggle and
  // never hear from, because nothing emits its type yet.
  const categories = visibleNotificationCategories(flags)
  const currentUser = useSelector((state) => state.currentUser)
  // Admin mode reads from the shared organizationUsers / organizationTags
  // slices — they're populated by the parent OrganizationPage for the
  // org the admin is configuring. User mode reads from the notifications
  // slice's own currentOrgUsers / currentOrgTags instead: when a Balto
  // admin viewing /organizations/5 opens their OWN preferences, the
  // shared slice still holds org 5's data, and the dropdown would show
  // the wrong users. The dedicated slice keeps this modal's filter
  // dropdowns isolated from whatever page mutated the shared slices.
  const adminOrgUsers = useSelector((state) => state.organizationUsers?.users || [])
  const adminOrgTags = useSelector((state) => state.organizationTags || [])
  const userOwnOrgUsers = useSelector((state) => state.notifications?.currentOrgUsers || [])
  const userOwnOrgTags = useSelector((state) => state.notifications?.currentOrgTags || [])
  const orgUsers = isAdminMode ? adminOrgUsers : userOwnOrgUsers
  const orgTags = isAdminMode ? adminOrgTags : userOwnOrgTags

  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [availableChannels, setAvailableChannels] = useState([])
  // Filter. Admin mode defaults to the whole org; user mode defaults to
  // the user's own subscription (see the loadSettings fallback paths).
  const [targetType, setTargetType] = useState(isAdminMode ? 'all_users' : 'specific_users')
  const [userIds, setUserIds] = useState([])
  const [tagIds, setTagIds] = useState([])
  // toggles[typeKey][channelKey] = bool — the rendered matrix state.
  const [toggles, setToggles] = useState({})
  const [managedByAdmin, setManagedByAdmin] = useState(false)

  const organizationId = currentUser?.organizationid

  // The user's currently-saved settings drive the initial form state.
  useEffect(() => {
    const loadSettings = async () => {
      try {
        setLoading(true)
        const [settingsResponse, channelsResponse] = await Promise.all([
          // Admin mode doesn't pre-fill from anyone's row — admin starts
          // from a blank matrix and applies it to N targets.
          isAdminMode
            ? Promise.resolve({ settings: null })
            : fetchingAPI(`${apiService.notification}/notifications/settings`, 'GET', dispatch),
          fetchingAPI(
            `${apiService.notification}/notifications/available-channels`,
            'GET',
            dispatch
          ),
        ])
        const channels = channelsResponse?.channels || ['in_app', 'email']
        setAvailableChannels(channels)

        const settings = settingsResponse?.settings
        if (settings) {
          setTargetType(inferTargetType(settings))
          setUserIds(settings.user_ids || [])
          setTagIds(settings.tag_ids || [])
          setManagedByAdmin(Boolean(settings.managed_by_admin))
          // Seed toggles from the stored events_matrix.
          const next = {}
          Object.entries(settings.events_matrix || {}).forEach(([typeKey, channelMap]) => {
            next[typeKey] = { ...channelMap }
          })
          setToggles(next)
        } else if (isAdminMode) {
          // Admin mode never pre-fills from a saved row: it starts blank
          // and applies the matrix to the N targets picked on the form
          // above. The filter ("which agents trigger") defaults to the
          // whole org until the admin narrows it.
          setTargetType('all_users')
          setUserIds([])
          setTagIds([])
          setToggles({})
          setManagedByAdmin(false)
        } else {
          // No saved row (backend now always seeds one, so this is the
          // defensive path). Default to the user's own subscription only,
          // matching the new product policy, not the whole org.
          const ownUserId = currentUser?.user_id
          setTargetType('specific_users')
          setUserIds(isNil(ownUserId) ? [] : [ownUserId])
          setTagIds([])
          setToggles({})
          setManagedByAdmin(false)
        }
      } catch (error) {
        // Fetch failed before any state was set: fall back to each mode's
        // default filter so admin mode never lands on specific_users.
        setTargetType(isAdminMode ? 'all_users' : 'specific_users')
        toast.error('Failed to load notification settings', { cause: error })
      } finally {
        setLoading(false)
      }
    }
    loadSettings()
    // currentUser?.user_id is here because the no-settings fallback
    // preselects the user's own id; if currentUser hydrates after first
    // mount, re-run so the own-user default actually applies.
  }, [isAdminMode, currentUser?.user_id])

  // Org users + tags drive the agent multi-select. Two paths:
  // - Admin mode: read from the shared organization slices. The parent
  //   OrganizationPage is the source of truth for the org being
  //   configured (org N from /organizations/N), and has already
  //   hydrated those slices. We deliberately do NOT fetch as a
  //   fallback here — `organizationId` is the admin's *own* org id
  //   (from currentUser), not the org they're configuring, so a
  //   fallback fetch would overwrite the slice with the wrong org's
  //   users and break the admin form. If the slice is somehow empty,
  //   the dropdowns stay empty; the admin can refresh the org page.
  // - User mode: dispatch fetchCurrentOrgFilterOptions, which populates
  //   the notifications slice's own currentOrgUsers / currentOrgTags.
  //   Going through the shared organization slices would surface stale
  //   data from whatever page the user just left.
  useEffect(() => {
    if (!organizationId || isAdminMode) {
      return
    }
    dispatch(fetchCurrentOrgFilterOptions(organizationId))
  }, [isAdminMode, organizationId])

  const userOptions = orgUsers.map((orgUser) => ({
    value: orgUser.id,
    label:
      orgUser.first_name || orgUser.last_name
        ? `${orgUser.first_name || ''} ${orgUser.last_name || ''}`.trim()
        : orgUser.username,
  }))
  const tagOptions = orgTags.map((tag) => ({ value: tag.id, label: tag.name }))

  const handleToggleRow = (typeKey) => {
    setToggles((prev) => {
      const wasOn = isRowEnabled(prev, typeKey, availableChannels)
      const nextRow = {}
      availableChannels.forEach((channel) => {
        // Off → on: turn ON every available channel (sane default).
        // On → off: turn OFF every channel (the master switch).
        nextRow[channel] = !wasOn
      })
      return { ...prev, [typeKey]: nextRow }
    })
  }

  const handleToggleCell = (typeKey, channel) => {
    setToggles((prev) => ({
      ...prev,
      [typeKey]: {
        ...(prev[typeKey] || {}),
        [channel]: !prev[typeKey]?.[channel],
      },
    }))
  }

  const handleSave = async () => {
    const matrix = buildEventsMatrix(toggles, ALL_NOTIFICATION_TYPES, availableChannels)
    const body = {
      all_users: targetType === 'all_users',
      user_ids: targetType === 'specific_users' ? userIds : [],
      tag_ids: targetType === 'tags' ? tagIds : [],
      events_matrix: matrix,
      enabled: true,
    }

    if (!body.all_users && isEmpty(body.user_ids) && isEmpty(body.tag_ids)) {
      toast.error('Pick at least one target — agents, tags, or all agents in the org')
      return
    }

    try {
      setSaving(true)
      if (isAdminMode) {
        const userIdTargets = adminTargetUserIds || []
        const tagIdTargets = adminTargetTagIds || []
        if (isEmpty(userIdTargets) && isEmpty(tagIdTargets)) {
          toast.error('Pick at least one user or tag in "Assign notification to"')
          return
        }
        await fetchingAPI(
          `${apiService.notification}/notifications/admin/settings`,
          'POST',
          dispatch,
          JSON.stringify({
            ...body,
            target_user_ids: userIdTargets,
            target_tag_ids: tagIdTargets,
          })
        )
        const targetCount = userIdTargets.length + tagIdTargets.length
        toast.success(
          `Notifications saved for ${targetCount} target${targetCount === 1 ? '' : 's'}`
        )
      } else {
        await fetchingAPI(
          `${apiService.notification}/notifications/settings`,
          'PUT',
          dispatch,
          JSON.stringify(body)
        )
        toast.success('Notification settings saved')
      }
      onSaved()
    } catch (error) {
      toast.error('Failed to save notification settings', { cause: error })
    } finally {
      setSaving(false)
    }
  }

  const selectedUserValues = userOptions.filter((option) => userIds.includes(option.value))
  const selectedTagValues = tagOptions.filter((option) => tagIds.includes(option.value))

  if (loading) {
    return <div className="notification-settings__loading">Loading…</div>
  }

  return (
    <div className="notification-settings">
      {/* Admin-managed indicator — only shown to the user, not in admin mode. */}
      {!isAdminMode && managedByAdmin && (
        <div className="notification-settings__admin-banner">
          These settings were configured by your organization admin. You can still edit them.
        </div>
      )}

      {/* Filter section — renders in both modes. In admin mode it sets
          the FILTER (which agents trigger notifications for the targets);
          the recipient picker ("Assign notification to") lives on the
          admin form one level up. */}
      <div className="notification-settings__filter">
        <h4 className="notification-settings__heading">Receive notifications from</h4>
        <SegmentedControl
          items={TARGET_OPTIONS.map((option) => ({
            label: option.label,
            accessor: option.key,
            active: targetType === option.key,
            onClick: () => setTargetType(option.key),
          }))}
        />

        {targetType === 'specific_users' && (
          <MultiSelect
            options={userOptions}
            value={selectedUserValues}
            placeholder="Search agents…"
            condenseOptions={false}
            onChange={(selected) => setUserIds((selected || []).map((option) => option.value))}
          />
        )}

        {targetType === 'tags' && (
          <MultiSelect
            options={tagOptions}
            value={selectedTagValues}
            placeholder="Search tags…"
            condenseOptions={false}
            onChange={(selected) => setTagIds((selected || []).map((option) => option.value))}
          />
        )}
      </div>

      <div className="notification-settings__matrix">
        <div className="notification-settings__matrix-header">
          <div className="notification-settings__matrix-events-col">Events</div>
          <div className="notification-settings__channel-labels">
            {availableChannels.map((channel) => (
              <span key={channel} className="notification-settings__channel-label">
                {CHANNEL_LABELS[channel] || channel}
              </span>
            ))}
          </div>
        </div>

        {categories.map((category) => (
          <div key={category.key} className="notification-settings__category">
            <div className="notification-settings__category-label">
              {category.label.toUpperCase()}
            </div>
            {category.types.map((type) => {
              const rowOn = isRowEnabled(toggles, type.key, availableChannels)
              return (
                <div key={type.key} className="notification-settings__row">
                  <Checkbox
                    className="notification-settings__row-label"
                    checked={rowOn}
                    onChange={() => handleToggleRow(type.key)}
                    label={type.label}
                    data-testid={`event-toggle-${type.key}`}
                  />
                  <div className="notification-settings__channel-cells">
                    {/*
                      Channel cells stay enabled even when the event row's
                      master checkbox is off — ticking a channel directly
                      auto-enables the row (rowOn derives from "any channel
                      true"). Lets the user opt in with one click instead
                      of toggling the row first.
                    */}
                    {availableChannels.map((channel) => (
                      <Checkbox
                        key={channel}
                        className="notification-settings__channel-cell"
                        checked={Boolean(toggles[type.key]?.[channel])}
                        onChange={() => handleToggleCell(type.key, channel)}
                        aria-label={`${type.label} via ${CHANNEL_LABELS[channel] || channel}`}
                        data-testid={`channel-toggle-${type.key}-${channel}`}
                      />
                    ))}
                  </div>
                </div>
              )
            })}
          </div>
        ))}
      </div>

      <div className="form-actions is-modal">
        <Button primary onClick={handleSave} disabled={saving}>
          {saving ? 'Saving…' : 'Save changes'}
        </Button>
      </div>
    </div>
  )
}
